// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, HTMLInputTypeAttribute, useState } from 'react';
import { Box, IconButton, InputAdornment, InputLabel, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseInput.module.scss';
import { icons } from '~/assets';

type Props = {
  label?: string;
  name?: string;
  width?: number | string;
  height?: number | string;
  type?: HTMLInputTypeAttribute;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  errorMessage?: string;
  multiline?: boolean;
  rows?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

const cx = classNames.bind(styles);

const BaseInput = (props: Props) => {
  //#region Destructuring Props
  const {
    label,
    name,
    width,
    height = 35,
    type,
    value,
    required = false,
    disabled = false,
    readOnly,
    multiline,
    rows,
    errorMessage,
    onChange,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowPassword = () => {
    setIsShowPassword((show) => !show);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseInputComponent' className={cx('inputContainer')}>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      {type === 'password' ? (
        <TextField
          fullWidth
          sx={{ width }}
          type={isShowPassword ? 'text' : 'password'}
          slotProps={{
            input: {
              className: cx('textFieldInput', { disabled: disabled }),
              sx: { height },
              readOnly,
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={handleShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge='end'
                    className={cx('passwordButton')}
                  >
                    {isShowPassword ? (
                      <img
                        src={icons.commonIconEyeHidden}
                        alt={t('common_alt_img')}
                        className={cx('passwordIcon')}
                      />
                    ) : (
                      <img
                        src={icons.commonIconEyeShow}
                        alt={t('common_alt_img')}
                        className={cx('passwordIcon')}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
            formHelperText: { className: cx('errorMessage') },
          }}
          name={name}
          value={value || EMPTY_STRING}
          disabled={disabled}
          onChange={onChange}
          error={!!errorMessage}
          helperText={errorMessage}
        />
      ) : (
        <TextField
          fullWidth
          sx={{ width }}
          type={type}
          slotProps={{
            input: {
              className: cx('textFieldInput', { disabled: disabled }, { multiline: multiline }),
              sx: { height },
              readOnly,
            },
            formHelperText: { className: cx('errorMessage') },
          }}
          placeholder={EMPTY_STRING}
          name={name}
          value={value || EMPTY_STRING}
          disabled={disabled}
          multiline={multiline}
          rows={rows}
          onChange={onChange}
          error={!!errorMessage}
          helperText={errorMessage}
        />
      )}
    </Box>
  );
};

export default BaseInput;
